<template>
  <div class="questionnaire">
    <div class="header">
      <div class="header-inner">
        <div class="header-content">
          <p class="header-title">
            <span class="highlight">簡単15秒！</span><br>
            <span>あなたにぴったりの業者を診断</span>
          </p>
        </div>
      </div>
    </div>

    <div class="question-body">
      <div v-if="!showResult && !loading" class="progress-bar">
        <div class="q-badge" :class="{ current: currentSlide === 1 }">Q1</div>
        <div class="q-badge" :class="{ current: currentSlide === 2 }">Q2</div>
        <div class="q-badge" :class="{ current: currentSlide === 3 }">Q3</div>
        <div class="q-badge" :class="{ current: currentSlide === 4 }">Q4</div>
        <div class="q-badge" :class="{ current: currentSlide === 5 }">Q5</div>
      </div>

      <div v-if="currentSlide === 1 && !loading && !showResult">
        <h2 class="question-title">Q1 お住まいのエリアは？</h2>
        <div class="options-grid">
          <button v-for="option in q1Options" :key="option" class="option-button"
            :class="{ active: answers[1] === option }" @click="selectAnswer(1, option)">
            {{ option }}
          </button>
        </div>
      </div>

      <div v-if="currentSlide === 2 && !loading && !showResult">
        <h2 class="question-title">Q2 設置する建物の種類は？</h2>
        <div class="options-grid">
          <button v-for="option in q2Options" :key="option" class="option-button"
            :class="{ active: answers[2] === option }" @click="selectAnswer(2, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 3 && !loading && !showResult">
        <h2 class="question-title">Q3 希望のメーカーは？</h2>
        <div class="options-grid">
          <button v-for="option in q3Options" :key="option" class="option-button"
            :class="{ active: answers[3] === option }" @click="selectAnswer(3, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 4 && !loading && !showResult">
        <h2 class="question-title">Q4 工事希望のスピードは？</h2>
        <div class="options-grid">
          <button v-for="option in q4Options" :key="option" class="option-button"
            :class="{ active: answers[4] === option }" @click="selectAnswer(4, option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
        </div>
      </div>

      <div v-if="currentSlide === 5 && !loading && !showResult">
        <h2 class="question-title">Q5 重視したいポイントは？<br>（複数選択可）</h2>
        <div class="options-grid">
          <button v-for="option in q5Options" :key="option" class="option-button"
            :class="{ active: selectedOptions.includes(option) }" :disabled="isDisabledOption(option)"
            @click="toggleOption(option)">
            {{ option }}
          </button>
        </div>
        <div class="button-group double">
          <button class="back-button" @click="backSlide">前の質問へ戻る</button>
          <button class="submit-button" @click="submit" :disabled="loading">診断結果を見る</button>
        </div>
      </div>

      <div v-if="loading" class="loading-box">
        <div class="spinner"></div>
        <p>あなたと業者の相性を診断中...</p>
      </div>

      <div v-if="showResult" class="result-box">
        <h2 class="section-title">あなたが選択した内容</h2>
        <ul class="answers-list">
          <li><strong>エリア :</strong> {{ answers[1] }}</li>
          <li><strong>建物の種類 :</strong> {{ answers[2] }}</li>
          <li><strong>メーカー :</strong> {{ answers[3] }}</li>
          <li><strong>工事スピード :</strong> {{ answers[4] }}</li>
          <li><strong>重視ポイント :</strong> {{ answers[5].join('、') || '特になし' }}</li>
        </ul>

        <h2 class="section-title">おすすめ業者とのマッチ度結果</h2>
        <div class="vendor-list">
          <div v-for="vendor in recommendedVendors" :key="vendor.name" class="vendor-card">
            <div class="vendor-header">
              <h3>{{ vendor.rank }}位：{{ vendor.name }}</h3>
              <span class="match-badge">あなたとのマッチ度：{{ vendor.match }}%</span>
            </div>

            <div class="match-bar">
              <div class="match-fill" :style="{ width: vendor.match + '%' }"></div>
            </div>

            <p v-if="vendor.reasonNote" class="reason-note">※ {{ vendor.reasonNote }}</p>

            <img :src="vendor.image" :alt="vendor.name" class="vendor-img" />
            <p class="vendor-caption">{{ vendor.caption }}</p>

            <ul class="vendor-features">
              <li v-for="(feature, idx) in vendor.features" :key="idx">
                <span v-html="feature"></span>
              </li>
            </ul>

            <div v-if="vendor.availabilityNote" class="availability-banner" role="status" aria-live="polite">
              <p class="line-1">あなたの住んでいるエリアは<strong>24時間即日対応</strong>可能エリアです。</p>
              <p class="line-2">本日{{ todayJa }}、<strong>すぐにご案内可能！</strong></p>
            </div>

            <div class="vendor-cta">
              <a :href="vendor.link" target="_blank" rel="nofollow noopener" class="vendor-button">無料見積もり依頼する</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import vendor1 from '@/assets/rank-table/vendor-1.png'
import vendor2 from '@/assets/rank-table/vendor-4-1.png'
import vendor3 from '@/assets/rank-table/vendor-6.png'
import { makeAffiliateUrl, makeAffiliateUrl2 } from '@/utils/aff.js'

const currentSlide = ref(1)
const answers = ref({ 1: '', 2: '', 3: '', 4: '', 5: [] })

const q1Options = ['北海道', '東北', '関東', '関西', '東海', '中国', '四国', '九州']
const q2Options = ['戸建て', 'マンション']
const q3Options = ['ノーリツ', 'リンナイ', 'パロマ', 'パーパス', 'ハーマン', 'その他', '特になし']
const q4Options = ['とにかく最短', '1週間以内', '未定']
const q5Options = ['費用の安さ', '施工実績', '口コミ評価', '保証内容', '対応スピード', '特になし']

const affiliateLink = computed(() => makeAffiliateUrl())
const affiliateLink2 = computed(() => makeAffiliateUrl2())

const selectedOptions = ref([])
const loading = ref(false)
const showResult = ref(false)
const recommendedVendors = ref([])

const now = ref(new Date())
let timerId = null
onMounted(() => {
  timerId = setInterval(() => { now.value = new Date() }, 30000)
})
onBeforeUnmount(() => {
  if (timerId) clearInterval(timerId)
})

const todayJa = computed(() => {
  const d = now.value
  const mm = d.getMonth() + 1
  const dd = d.getDate()
  const HH = d.getHours()
  const MM = String(d.getMinutes()).padStart(2, '0')
  return `${mm}月${dd}日 ${HH}時${MM}分`
})

const selectAnswer = (qNum, value) => {
  answers.value[qNum] = value
  currentSlide.value++
}

const backSlide = () => {
  if (currentSlide.value > 1) currentSlide.value--
}

const toggleOption = (option) => {
  if (selectedOptions.value.includes(option)) {
    selectedOptions.value = selectedOptions.value.filter(o => o !== option)
  } else {
    if (option === '特になし') {
      selectedOptions.value = ['特になし']
    } else {
      selectedOptions.value = selectedOptions.value.filter(o => o !== '特になし')
      selectedOptions.value.push(option)
    }
  }
  answers.value[5] = selectedOptions.value
}

const isDisabledOption = (option) => {
  if (option === '特になし' && selectedOptions.value.length > 0 && !selectedOptions.value.includes('特になし')) return true
  if (selectedOptions.value.includes('特になし') && option !== '特になし') return true
  return false
}

const submit = () => {
  loading.value = true
  showResult.value = false
  setTimeout(() => {
    recommendedVendors.value = calculateVendorsWithMatch()
    loading.value = false
    showResult.value = true
  }, 500)
}

const vendorProfiles = [
  {
    key: 'kaketsuke',
    name: '給湯器駆けつけ隊',
    image: vendor1,
    link: null,
    features: [
      '<span class="bold-red">最大91%OFF！</span>業界最安値クラスの料金',
      '<span class="bold-red">最短30分で駆けつけ！</span>即日対応OK！',
      'Google<span class="bold-red">口コミ4.8</span>',
      '工事・商品の<span class="bold-red">無料10年保証</span>'
    ],
    baseCaption: '安さと対応スピードを重視する方におすすめ'
  },
  {
    key: 'oyudenai',
    name: 'お湯が出ない.com',
    image: vendor2,
    link: null,
    features: [
      '交換だけではなく<span class="bold-red">修理での対応も可能！</span>',
      '修理で済んだ場合<span class="bold-red">交換より圧倒的な格安！</span>',
      '今だけ<span class="bold-red">給湯器交換20%OFF</span>'
    ],
    baseCaption: '修理も検討している方におすすめ'
  },
  {
    key: 'chikaramochi',
    name: 'チカラもち',
    image: vendor3,
    link: 'https://chikaramoti.co.jp/',
    features: [
      '写真を送るだけで、<span class="bold-red">サクッと見積り可能！</span>',
      '見積り後の<span class="bold-red">追加費用は一切なし！</span>',
      '関西在住の方は<span class="bold-red">すぐに駆けつけ</span>'
    ],
    baseCaption: '関西エリアに在住している方におすすめ'
  }
]

const calculateVendorsWithMatch = () => {
  // ✅ 基本は100%
  const base = { kaketsuke: 100, oyudenai: 100, chikaramochi: 100 }
  const notes = { kaketsuke: '', oyudenai: '', chikaramochi: '' }
  const availability = { kaketsuke: false }

  const area = answers.value[1]
  const speed = answers.value[4]
  const prefs = new Set(answers.value[5] || [])
  const noPref = prefs.has('特になし')

  // =========================
  // ✅ 給湯器駆けつけ隊（今のロジックでOK）
  // =========================
  const kaketsuke24hAreas = new Set(['北海道', '東北', '関東', '東海', '関西'])
  if (area && kaketsuke24hAreas.has(area)) availability.kaketsuke = true

  if (!noPref) {
    // 今までのロジック：施工実績を重視 → 駆けつけ隊は少し減点
    if (prefs.has('施工実績')) base.kaketsuke -= 3
  }

  const speedPriority = speed === 'とにかく最短' || (!noPref && prefs.has('対応スピード'))
  if (area && !kaketsuke24hAreas.has(area) && speedPriority) base.kaketsuke -= 4

  // =========================
  // ✅ お湯が出ない.com
  //  - 関東以外は0% + 対応外
  //  - 重視ポイント：各 -3
  //  - link は makeAffiliateUrl2
  // =========================
  if (area && area !== '関東') {
    base.oyudenai = 0
    notes.oyudenai = 'あなたの住んでいる地域は対応外エリアです'
  } else {
    if (!noPref) {
      if (prefs.has('費用の安さ')) base.oyudenai -= 3
      if (prefs.has('施工実績')) base.oyudenai -= 3
      if (prefs.has('口コミ評価')) base.oyudenai -= 3
      if (prefs.has('保証内容')) base.oyudenai -= 3
    }
  }

  // =========================
  // ✅ チカラもち
  //  - 関西以外は0% + 対応外
  //  - とにかく最短：-4
  //  - 重視ポイント：指定のみ各 -3
  // =========================
  if (area && area !== '関西') {
    base.chikaramochi = 0
    notes.chikaramochi = 'あなたの住んでいる地域は対応外エリアです'
  } else {
    if (speed === 'とにかく最短') base.chikaramochi -= 4

    if (!noPref) {
      if (prefs.has('費用の安さ')) base.chikaramochi -= 3
      if (prefs.has('保証内容')) base.chikaramochi -= 3
      if (prefs.has('対応スピード')) base.chikaramochi -= 3
    }
  }

  // ✅ 0未満は0に丸める
  base.kaketsuke = Math.max(0, Math.round(base.kaketsuke))
  base.oyudenai = Math.max(0, Math.round(base.oyudenai))
  base.chikaramochi = Math.max(0, Math.round(base.chikaramochi))

  return vendorProfiles
    .map(v => ({
      name: v.name,
      image: v.image,
      link:
        v.key === 'kaketsuke' ? affiliateLink.value
          : v.key === 'oyudenai' ? affiliateLink2.value
            : v.link,
      features: v.features,
      caption: v.baseCaption,
      match: base[v.key],
      reasonNote: notes[v.key] || '',
      availabilityNote: v.key === 'kaketsuke' && availability.kaketsuke && base[v.key] > 0
    }))
    .sort((a, b) => b.match - a.match)
    .map((v, i) => ({ ...v, rank: i + 1 }))
}
</script>

<style scoped>
.questionnaire {
  --ink: #1f2a44;
  --muted: #5b6476;
  --brand: #1f63b8;
  --brand2: #3f8de0;
  --accent: #ff8a00;
  --accent2: #ff5a5a;
  --bg: #f6f8ff;
  --card: #ffffff;
  --line: rgba(31, 42, 68, .12);
  --shadow-soft: 0 10px 20px rgba(0, 0, 0, .06);
  margin: 15px;
  border: 1px solid rgb(190, 187, 187);
  overflow: hidden;
  background: #fff;
}

.header {
  background: #1f63b8;
  ;
  color: #fff;
  padding: 18px 16px;
  text-align: center;
}

.header-title {
  margin: 0;
  color: #fff;
  letter-spacing: .02em;
  font-weight: 1000;
  font-size: 1.3rem;
  line-height: 1.35;
}

.highlight {
  color: #ffeb3b;
  font-size: 1.5rem;
  font-weight: 1000;
}

.question-body {
  background: linear-gradient(180deg, var(--bg) 0%, #fff 70%);
  padding: 18px 18px 22px;
}

.progress-bar {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 2px 0 14px;
}

.q-badge {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 1000;
  color: #fff;
  background: rgba(31, 42, 68, .25);
  border: 1px solid rgba(255, 255, 255, .35);
}

.q-badge.current {
  background: linear-gradient(180deg, #ffa043 0%, var(--accent) 55%, #e96e00 100%);
  border-color: rgba(233, 110, 0, .55);
}

.question-title {
  font-size: 1.2rem;
  font-weight: 1000;
  text-align: center;
  color: var(--ink);
  margin: 6px 0 14px;
}

.options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.option-button {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 10px 12px 10px 44px;
  border-radius: 14px;
  border: 1px solid rgba(31, 42, 68, .18);
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
  font-size: 0.85rem;
  font-weight: 900;
  color: var(--ink);
  cursor: pointer;
  transition: transform .08s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease;
  text-align: left;
}

.option-button::before {
  content: "";
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid rgba(31, 99, 184, .35);
  background: #fff;
}

.option-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(0, 0, 0, .08);
  border-color: rgba(31, 99, 184, .35);
}

.option-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 8px 18px rgba(0, 0, 0, .05);
}

.option-button.active {
  background: #4154bf;
  color: #fff;
  transform: translateY(-1px);
}

.option-button.active::after {
  content: "";
  position: absolute;
  left: 18px;
  top: 50%;
  width: 6px;
  height: 11px;
  border-right: 3px solid #18a957;
  border-bottom: 3px solid #18a957;
  transform: translateY(-55%) rotate(40deg);
}

.button-group {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
}

.button-group.double {
  justify-content: space-between;
}

.back-button,
.submit-button {
  flex: 1;
  border-radius: 999px;
  padding: 12px 14px;
  font-size: 15px;
  font-weight: 1000;
  cursor: pointer;
  border: 1px solid rgba(31, 42, 68, .18);
}

.back-button {
  background: #fff;
  color: var(--muted);
}

.back-button:hover {
  filter: brightness(0.98);
}

.submit-button {
  border: 2px solid rgba(233, 110, 0, .55);
  background: #f0841a;
  color: #fff;
}

.submit-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(233, 110, 0, .28);
  filter: brightness(1.02);
}

.submit-button:disabled {
  opacity: .7;
  cursor: default;
  transform: none;
}

.loading-box {
  text-align: center;
  font-size: 16px;
  font-weight: 900;
  color: var(--brand);
  margin: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.spinner {
  border: 4px solid rgba(31, 99, 184, .14);
  border-top: 4px solid var(--accent);
  border-radius: 50%;
  width: 44px;
  height: 44px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.section-title {
  font-size: 18px;
  font-weight: 1000;
  color: var(--brand);
  border-bottom: 1px solid rgba(31, 99, 184, .25);
  padding-bottom: 8px;
  margin: 18px 0 12px;
}

.answers-list {
  list-style: none;
  padding: 12px;
  margin: 0 0 16px;
  background: #fff;
  border: 1px solid rgba(31, 42, 68, .10);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.answers-list li {
  background: #fff;
  border: 1px solid rgba(31, 42, 68, .10);
  padding: 10px 12px;
  font-size: 14px;
  color: var(--ink);
  text-align: left;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
}

.answers-list li::before {
  content: "";
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(31, 99, 184, .75);
  background: #fff;
  position: relative;
  flex: 0 0 auto;
}

.answers-list li::after {
  content: "";
  position: absolute;
  left: 18px;
  top: 50%;
  width: 5px;
  height: 9px;
  border-right: 3px solid #18a957;
  border-bottom: 3px solid #18a957;
  transform: translateY(-55%) rotate(40deg);
  opacity: 1;
  pointer-events: none;
}

.answers-list li strong {
  color: var(--brand);
  font-weight: 1000;
  margin-right: 6px;
  min-width: 92px;
}

.vendor-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-top: 10px;
}

.vendor-card {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
}

.vendor-header {
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #1f63b8;
  color: #fff;
  flex-wrap: wrap;
}

.vendor-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 1000;
}

.match-badge {
  background: #fff;
  border: 1px solid rgba(255, 255, 255, .28);
  color: #1f63b8;
  padding: 7px 12px;
  border-radius: 999px;
  font-weight: 1000;
  font-size: 13px;
  white-space: nowrap;
}

.match-bar {
  grid-column: 1 / -1;
  width: 100%;
  height: 10px;
  background: rgba(31, 99, 184, .12);
  overflow: hidden;
}

.match-fill {
  height: 100%;
  background: #e0aa07;
  border-radius: 0 10px 10px 0;
}

.reason-note {
  grid-column: 1 / -1;
  margin: 10px 14px 0;
  color: #c62828;
  font-size: 14px;
  font-weight: 900;
}

.vendor-img {
  grid-column: 1 / 2;
  width: calc(100% - 28px);
  margin: 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, .06);
  box-shadow: 0 12px 22px rgba(0, 0, 0, .08);
  background: #fff;
  height: auto;
  display: block;
}

.vendor-caption {
  grid-column: 2 / 3;
  margin: 14px 14px 10px;
  padding: 12px 12px;
  background: rgba(31, 99, 184, .08);
  border: 1px solid rgba(31, 99, 184, .18);
  border-radius: 14px;
  color: var(--ink);
  font-size: 0.85rem;
  line-height: 1.6;
  font-weight: 600;
}

.vendor-features {
  grid-column: 2 / 3;
  list-style: none;
  padding: 0 14px;
  margin: 0 0 20px;
  font-size: 14px;
  color: var(--ink);
  font-weight: 800;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
}

.vendor-features li {
  position: relative;
  padding-left: 30px;
  line-height: 1.6;
  border-bottom: 1px solid rgba(31, 42, 68, .10);
  padding-bottom: 8px;
}

.vendor-features li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.vendor-features li::before {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(31, 99, 184, .75);
  background: #fff;
}

.vendor-features li::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 5px;
  width: 5px;
  height: 9px;
  border-right: 3px solid #18a957;
  border-bottom: 3px solid #18a957;
  transform: rotate(40deg);
}

.vendor-cta {
  grid-column: 2 / 3;
  padding: 0 14px 16px;
  display: flex;
  justify-content: center;
}

.vendor-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: min(520px, 100%);
  padding: 14px 18px;
  border-radius: 999px;
  border: 2px solid rgba(233, 110, 0, .55);
  background: linear-gradient(180deg, #ffa043 0%, var(--accent) 55%, #e96e00 100%);
  color: #fff;
  text-decoration: none;
  font-weight: 1000;
  letter-spacing: .02em;
  line-height: 1;
  box-sizing: border-box;
  text-align: center;
}

.vendor-button::after {
  content: "▶";
  font-size: .95em;
  translate: 0 1px;
}

.vendor-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 30px rgba(233, 110, 0, .28);
  filter: brightness(1.02);
}

.vendor-button:active {
  transform: translateY(0);
  box-shadow: 0 10px 18px rgba(233, 110, 0, .20);
}

.vendor-button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px #fff, 0 0 0 6px rgba(255, 122, 0, .9);
}

.bold-red {
  color: #d32f2f;
  font-weight: 1000;
}

.sp-simple-badge {
  display: none;
}

/* ✅ 追加（availability banner）：派手すぎない注意喚起 */
.availability-banner {
  margin: 0 14px 14px;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(31, 99, 184, .18);
  background: rgb(255 239 189);
  color: var(--ink);
  font-weight: 600;
  line-height: 1.6;
  text-align: left;
  font-size: 0.85rem;
}

.availability-banner .line-1,
.availability-banner .line-2 {
  margin: 0;
}

.availability-banner .line-2 {
  margin-top: 6px;
}

.availability-banner strong {
  color: #d32f2f;
  font-weight: 1000;
}

.submit-button,
.vendor-button {
  position: relative;
  overflow: hidden;
  isolation: isolate;
}

.submit-button::before,
.vendor-button::before {
  content: "";
  position: absolute;
  /* ✅ 縦方向を“確実に”全面カバー（多段テキストでもOK） */
  top: -140%;
  left: -80%;
  height: 380%;
  width: 60%;

  z-index: 2;
  pointer-events: none;

  background: linear-gradient(
    90deg,
    rgba(255,255,255,0) 0%,
    rgba(255,255,255,.35) 35%,
    rgba(255,255,255,.95) 50%,
    rgba(255,255,255,.35) 65%,
    rgba(255,255,255,0) 100%
  );

  transform: translateX(-220%) skewX(-20deg);
  opacity: 0;
  mix-blend-mode: screen;
  filter: blur(.25px);
  animation: btnShine 3.8s ease-in-out infinite;
}

.submit-button:hover::before,
.vendor-button:hover::before {
  animation-duration: 2.6s;
}

@media (prefers-reduced-motion: reduce) {
  .submit-button::before,
  .vendor-button::before {
    animation: none;
    display: none;
  }
}

/* ====== responsive ====== */
@media (max-width: 980px) {
  .questionnaire {
    margin: 20px 10px;
  }
}

@media (max-width: 820px) {
  .vendor-card {
    grid-template-columns: 1fr;
  }

  .vendor-img {
    grid-column: 1 / -1;
    width: calc(100% - 28px);
  }

  .vendor-caption,
  .vendor-features,
  .vendor-cta {
    grid-column: 1 / -1;
  }
}

@media (max-width: 600px) {
  .sp-simple-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, .16);
    border: 1px solid rgba(255, 255, 255, .28);
    color: #fff;
    padding: 6px 14px;
    font-weight: 1000;
    border-radius: 999px;
    font-size: 12px;
    margin-bottom: 8px;
  }

  .question-body {
    padding: 14px 12px 18px;
  }

  .progress-bar {
    gap: 8px;
  }

  .q-badge {
    width: 38px;
    height: 38px;
    font-size: 12px;
  }

  .options-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .option-button {
    font-size: 0.8rem;
    min-height: 46px;
  }

  .button-group.double {
    flex-direction: column;
  }
}
</style>
